import { useEffect, useRef, useState, type RefObject } from "react";
import type { PlaybackState } from "../types/stream";

const EVENT_STATE: ReadonlyArray<readonly [string, PlaybackState]> = [
  ["playing", "playing"],
  ["pause", "paused"],
  ["waiting", "stalled"],
  ["stalled", "stalled"],
  ["error", "error"],
  ["emptied", "idle"],
];

export interface MediaElementStatus {
  playback: PlaybackState;
  /** Autoplay was refused; starting playback needs a user gesture. */
  blocked: boolean;
  /** Starts playback from a user gesture — the tap-to-play affordance. */
  play(): void;
}

/**
 * What a media element is actually doing, as opposed to what its WHEP session
 * says. Reports through `onChange` on real element events only — never on
 * attach or detach, so an element going away cannot overwrite the state of the
 * surface that replaced it.
 */
export function useMediaElementStatus(
  ref: RefObject<HTMLMediaElement | null>,
  onChange?: (playback: PlaybackState) => void,
): MediaElementStatus {
  const [playback, setPlayback] = useState<PlaybackState>("idle");
  const [blocked, setBlocked] = useState(false);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const attached = useRef<HTMLMediaElement | null>(null);
  const detach = useRef<(() => void) | null>(null);
  const lastSource = useRef<MediaProvider | null>(null);

  // No dependency array, on purpose — the same reason as the srcObject effects
  // in useWhepPlayer and PipCard. The element behind a stable ref object is
  // replaced when the tile swaps its <video> for a placeholder, or when the
  // card is portalled into another document, and a keyed effect would never
  // notice. The comparisons below keep this cheap and idempotent.
  useEffect(() => {
    const el = ref.current;

    if (el !== attached.current) {
      detach.current?.();
      detach.current = null;
      attached.current = el;
      lastSource.current = null;
      // A new element starts from a clean slate — stale "playing"/blocked
      // state from whatever this ref used to point at must not leak onto it.
      setPlayback("idle");
      setBlocked(false);
      if (el) {
        const report = (next: PlaybackState) => {
          setPlayback(next);
          if (next === "playing") setBlocked(false);
          onChangeRef.current?.(next);
        };
        const handlers = EVENT_STATE.map(([type, state]) => {
          const handler = (ev: Event) => {
            // A removed element can still queue a trailing event (e.g. a
            // browser-native "pause" when it leaves the document) that fires
            // after it's no longer the one on screen — ignore it.
            const target = ev.target as Node | null;
            if (target && !target.isConnected) return;
            report(state);
          };
          el.addEventListener(type, handler);
          return [type, handler] as const;
        });
        detach.current = () => {
          for (const [type, handler] of handlers)
            el.removeEventListener(type, handler);
        };
      }
    }

    // No DOM event says "autoplay was refused": call play() and catch it.
    if (el && el.srcObject && el.srcObject !== lastSource.current) {
      lastSource.current = el.srcObject;
      el.play()?.catch((err: unknown) => {
        if ((err as { name?: string } | null)?.name !== "NotAllowedError")
          return;
        setBlocked(true);
        setPlayback("paused");
        onChangeRef.current?.("paused");
      });
    }
  });

  useEffect(
    () => () => {
      detach.current?.();
      detach.current = null;
      // Reset the re-attach gate too: StrictMode (dev only) runs this cleanup
      // then replays the no-deps effect above on the same mount, refs kept.
      // Without this, the replay sees `el === attached.current` and skips
      // re-attaching, leaving the element listener-less for the rest of its
      // life.
      attached.current = null;
      lastSource.current = null;
    },
    [],
  );

  return {
    playback,
    blocked,
    play: () => {
      attached.current
        ?.play()
        ?.then(() => setBlocked(false))
        .catch(() => {
          /* still refused; the affordance stays up */
        });
    },
  };
}
